import { useCallback, useMemo, useState } from "react";

import { type NgwWebmapItem, fetchNgwExtent } from "@nextgis/ngw-kit";
import { MapControl } from "@nextgis/react-ngw-map";
import ReactNgwOl from "@nextgis/react-ngw-ol";
import type { LngLatBoundsArray } from "@nextgis/utils";

import { MapWorkspaceContext } from "./MapWorkspaceContext";
import { LayerPanel } from "./components/LayerPanel";
import { MapAttributionBar } from "./components/MapAttributionBar";
import { SearchPanel } from "./components/SearchPanel";
import { ZoomControl } from "./components/map-controls/ZoomControl";
import { MapIdentification } from "./components/map-identification/MapIdentification";
import { TranslationProvider, type TranslationProviderOptions } from "./i18n";
import type {
  MapToolState,
  NgwAuth,
  NgwMapInstance,
  SidebarFooter,
  ToolMode,
} from "./types";

import "./MapWorkspace.css";
import "./styles.css";

export interface MapWorkspaceProps {
  auth?: NgwAuth;
  className?: string;
  hiddenLayerIds?: number[];
  identificationHighlightLayerId?: string;
  ngwUrl: string;
  searchResourceId?: number;
  sidebarFooter?: SidebarFooter;
  translation?: TranslationProviderOptions;
  webmapAdapterOptions?: Record<string, unknown>;
  webmapId: number;
  webmapLayerId?: string;
}

function renderSidebarFooter(
  footer: SidebarFooter | undefined,
  state: MapToolState,
) {
  return typeof footer === "function" ? footer(state) : footer;
}

export function MapWorkspace({
  auth,
  className,
  hiddenLayerIds = [],
  identificationHighlightLayerId = "identify-highlight",
  ngwUrl,
  searchResourceId,
  sidebarFooter,
  translation,
  webmapAdapterOptions,
  webmapId,
  webmapLayerId = `webmap-${webmapId}`,
}: MapWorkspaceProps) {
  const [ngwMap, setNgwMap] = useState<NgwMapInstance | null>(null);
  const [webmapRoot, setWebmapRoot] = useState<NgwWebmapItem | null>(null);
  const [activeMode, setActiveMode] = useState<ToolMode | null>(null);
  const [extent, setExtent] = useState<LngLatBoundsArray | undefined>(
    undefined,
  );

  const handleMapCreated = useCallback(
    async (map: NgwMapInstance) => {
      setNgwMap(map);

      const layerAdapter = await map.addNgwLayer({
        resource: webmapId,
        id: webmapLayerId,
        adapterOptions: {
          selectable: true,
          useBasemap: false,
          defaultBasemap: true,
          ...webmapAdapterOptions,
        },
      });

      const webmapExtent = await fetchNgwExtent({
        connector: map.connector,
        resourceId: webmapId,
      });

      if (webmapExtent) {
        setExtent(webmapExtent);
        map.fitBounds(webmapExtent);
      }

      if (layerAdapter) {
        setWebmapRoot(layerAdapter.layer || null);
      }
    },
    [webmapAdapterOptions, webmapId, webmapLayerId],
  );

  const updateActiveMode = useCallback((mode: ToolMode, status: boolean) => {
    setActiveMode((current) => {
      if (status) return mode;
      return current === mode ? null : current;
    });
  }, []);

  const contextValue = useMemo(() => ({ ngwMap }), [ngwMap]);
  const toolState = useMemo(
    () => ({ activeMode, updateActiveMode }),
    [activeMode, updateActiveMode],
  );
  const rootClassName = ["map-app", className].filter(Boolean).join(" ");

  return (
    <TranslationProvider {...translation}>
      <MapWorkspaceContext value={contextValue}>
        <div className={rootClassName}>
          <div className="map-app-body">
            <LayerPanel
              footer={renderSidebarFooter(sidebarFooter, toolState)}
              hiddenLayerIds={hiddenLayerIds}
              root={webmapRoot}
            />

            <main className="map-shell">
              <ReactNgwOl
                className="map"
                baseUrl={ngwUrl}
                auth={auth}
                osm={true}
                controls={[]}
                highlightIdentification={false}
                whenCreated={handleMapCreated}
              >
                <MapIdentification
                  activeMode={activeMode}
                  highlightLayerId={identificationHighlightLayerId}
                />
                <ZoomControl order={10} extent={extent} position="top-left" />
                <MapAttributionBar />
                {searchResourceId && (
                  <MapControl
                    className="search-panel"
                    position="top-right"
                    order={40}
                    margin
                  >
                    <SearchPanel searchResourceId={searchResourceId} />
                  </MapControl>
                )}
              </ReactNgwOl>
            </main>
          </div>
        </div>
      </MapWorkspaceContext>
    </TranslationProvider>
  );
}
